import React, { useMemo, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { FormattedMessage } from 'react-intl';

import { IItem } from 'api/catalog/models';
import { getSafeIndex } from 'utils/getSafeIndex';

import messages from './messages';
import ImageInformation from './ImageInformation';
import ImageTags from './ImageTags';

export const useStyles = makeStyles(() =>
  createStyles({
    imageWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: 300,
      borderRadius: 10,
      flexDirection: 'column',
      flexGrow: 1,
      position: 'relative',
    },
    imageBox: {
      position: 'relative',
      border: '2px solid #ececec',
      padding: '10px 50px',
      borderRadius: '20px',
      textAlign: 'center',
    },
    leftCarouselBtn: {
      position: 'absolute',
      top: '50%',
      left: '8px',
      marginTop: '-16px',
      width: '32px',
      height: '32px',
      borderRadius: '32px',
      backgroundColor: '#0f3d61',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    rightCarouselBtn: {
      position: 'absolute',
      top: '50%',
      right: '8px',
      marginTop: '-16px',
      width: '32px',
      height: '32px',
      borderRadius: '32px',
      backgroundColor: '#0f3d61',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    carousel: {
      marginTop: '20px',
      display: 'flex',
    },
    carouselDot: {
      width: '8px',
      height: '8px',
      border: 'solid 2px #6f7784',
      boxSizing: 'border-box',
      borderRadius: '8px',
      marginRight: '8px',
      cursor: 'pointer',
    },
    productImage: {
      width: 'auto !important',
      maxWidth: '100%',
      maxHeight: '500px',
      userSelect: 'none',
    },
    carouselDotSelected: {
      width: '8px',
      height: '8px',
      backgroundColor: '#0f3d61',
      borderRadius: '8px',
      marginRight: '8px',
      cursor: 'pointer',
    },
    hiddenImage: {
      userSelect: 'none',
      display: 'none',
    },
  })
);

interface IProps {
  assets: IItem['assets'];
  initialIndex?: number;
}

const ImageCarousel = ({ assets = [], initialIndex = 0 }: IProps) => {
  const classes = useStyles();
  const [carouselIndex, setCarouselIndex] = useState(
    getSafeIndex(assets, initialIndex)
  );
  const currentAsset = useMemo(
    () =>
      assets.length && carouselIndex !== undefined
        ? assets[carouselIndex]
        : undefined,
    [assets, carouselIndex]
  );

  const goBack = useCallback(() => {
    setCarouselIndex((prev = 0) => (prev === 0 ? assets.length - 1 : prev - 1));
  }, [assets.length]);

  const goForward = useCallback(() => {
    setCarouselIndex((prev = 0) => {
      return prev >= assets.length - 1 ? 0 : prev + 1;
    });
  }, [assets.length]);

  return currentAsset && currentAsset.source && carouselIndex !== undefined ? (
    <Box className={classes.imageWrapper}>
      <div className={classes.imageBox}>
        <ImageTags asset={currentAsset} />
        {assets && assets.length > 1 && (
          <>
            <div
              role='button'
              onClick={goBack}
              className={classes.leftCarouselBtn}
            >
              <svg
                className='MuiSvgIcon-root'
                focusable='false'
                viewBox='0 0 24 24'
                aria-hidden='true'
              >
                <path
                  fill='#FFFFFF'
                  d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'
                ></path>
              </svg>
            </div>
            <div
              role='button'
              onClick={goForward}
              className={classes.rightCarouselBtn}
            >
              <svg
                className='MuiSvgIcon-root'
                focusable='false'
                viewBox='0 0 24 24'
                aria-hidden='true'
              >
                <path
                  fill='#FFFFFF'
                  d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'
                ></path>
              </svg>
            </div>
          </>
        )}
        <img
          title={currentAsset.source}
          src={currentAsset.source}
          alt='product image'
          className={classes.productImage}
        />
        <Box display='flex' alignItems='center' justifyContent='center'>
          <ImageInformation asset={currentAsset} />
        </Box>
      </div>
      {assets && assets.length > 1 && (
        <Box className={classes.carousel}>
          {assets?.map((asset, index) => (
            <div
              role='button'
              onClick={() => {
                setCarouselIndex(index);
              }}
              key={'carousel-image' + index}
              className={
                index === carouselIndex
                  ? classes.carouselDotSelected
                  : classes.carouselDot
              }
            >
              <img
                title={asset.source}
                src={asset.source}
                className={classes.hiddenImage}
              />
            </div>
          ))}
        </Box>
      )}
    </Box>
  ) : (
    <div>
      <FormattedMessage {...messages.noImage} />
    </div>
  );
};

export default ImageCarousel;
